import { useEffect } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';

export default function PrivacyPage() {
  useEffect(() => { document.title = 'Integritetspolicy | H.J.Ä.L.P.'; }, []);
  return (
    <div className="py-12 px-6">
      <div className="max-w-4xl mx-auto">
        <Card>
          <CardHeader>
            <h1 className="text-3xl font-bold tracking-tight">Integritetspolicy</h1>
            <p className="text-sm text-muted-foreground mt-2">
              Senast uppdaterad: {new Date().toLocaleDateString('sv-SE')}
            </p>
          </CardHeader>
          <CardContent className="prose prose-sm max-w-none space-y-6">
            <section className="bg-muted/50 rounded-lg p-4 border-l-4 border-primary">
              <p className="text-sm font-medium">
                H.J.Ä.L.P. är ett pedagogiskt stödprogram — inte hälso- och sjukvård.
                Personuppgifter behandlas av operatören av H.J.Ä.L.P. (sinnerjin.com).
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">1. Inledning</h2>
              <p>
                Denna integritetspolicy beskriver hur H.J.Ä.L.P.-portalen samlar in, använder, lagrar och
                skyddar dina personuppgifter. Vi följer Dataskyddsförordningen (GDPR) och svensk
                dataskyddslagstiftning.
              </p>
              <p className="mt-2">
                <strong>Personuppgiftsansvarig:</strong> Operatören av H.J.Ä.L.P. — sinnerjin.com<br />
                <strong>Kontakt:</strong> Via kontaktformuläret på sinnerjin.com
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">2. Vilka Personuppgifter Samlar Vi In?</h2>

              <h3 className="text-lg font-medium mt-4 mb-2">2.1 Kontouppgifter</h3>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Namn och användarnamn</li>
                <li>E-postadress</li>
                <li>Användarroll (klient, handledare, administratör)</li>
                <li>Inloggningsaktivitet och tidsstämplar</li>
              </ul>

              <h3 className="text-lg font-medium mt-4 mb-2">2.2 Bedömningsdata</h3>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Svar på Formulär 1 (119 frågor över 15 domäner)</li>
                <li>Svar på Formulär 2 (82 fördjupade frågor)</li>
                <li>Beräknade riskpoäng (WRS, RI, Holistisk Risk)</li>
                <li>Identifierade fokusområden och styrkor</li>
                <li>Programrekommendationer</li>
              </ul>

              <h3 className="text-lg font-medium mt-4 mb-2">2.3 Sessionsdata</h3>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Svar på övningar i 48 sessioner</li>
                <li>Genomförda sessioner och tidpunkter</li>
                <li>Framsteg genom programmet</li>
                <li>Handledarens anteckningar (om tillämpligt)</li>
              </ul>

              <h3 className="text-lg font-medium mt-4 mb-2">2.4 Teknisk data</h3>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>IP-adress</li>
                <li>Webbläsartyp och version</li>
                <li>Enhetsinformation</li>
                <li>Användaraktivitet inom plattformen</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">3. Laglig Grund för Behandling</h2>
              <p className="mb-2">
                Vi behandlar dina personuppgifter baserat på:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li><strong>Samtycke (art. 6.1.a):</strong> Du samtycker till behandling vid registrering</li>
                <li><strong>Uttryckligt samtycke (art. 10):</strong> Separat samtycke för uppgifter om lagöverträdelser</li>
                <li><strong>Allmänt intresse (art. 6.1.e):</strong> Databehandling nödvändig för rehabiliteringsändamål</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">4. Hur Använder Vi Dina Personuppgifter?</h2>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Genomföra riskbedömningar och generera programrekommendationer</li>
                <li>Tillhandahålla personaliserade KBT-sessioner</li>
                <li>Spåra och dokumentera ditt framsteg genom programmet</li>
                <li>Möjliggöra kommunikation mellan klient och handledare</li>
                <li>Förbättra plattformens funktionalitet och innehåll</li>
                <li>Uppfylla rättsliga krav på dokumentation</li>
                <li>Säkerställa systemets säkerhet och integritet</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">5. Delning av Personuppgifter</h2>
              <p className="mb-2">
                Dina personuppgifter delas <strong>endast</strong> med:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Din tilldelade handledare inom programmet</li>
                <li>Administratörer med behörighet inom systemet</li>
                <li>Tekniska leverantörer som tillhandahåller infrastruktur (med databehandlingsavtal)</li>
              </ul>
              <p className="mt-2">
                Vi delar <strong>aldrig</strong> dina personuppgifter med tredje part för marknadsföringsändamål.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">6. Lagring och Säkerhet</h2>

              <h3 className="text-lg font-medium mt-4 mb-2">6.1 Var lagras data?</h3>
              <p>
                All data lagras krypterat i säkra datacenter inom EU/EES-området. Vi använder Supabase
                som teknisk plattform med servrar i EU (Frankfurt, eu-central-1).
              </p>

              <h3 className="text-lg font-medium mt-4 mb-2">6.2 Säkerhetsåtgärder</h3>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Kryptering av data i transit (TLS/SSL) och i vila</li>
                <li>Multifaktorautentisering för handledare och administratörer</li>
                <li>Rollbaserad åtkomstkontroll (RLS)</li>
                <li>Regelbundna säkerhetskopior</li>
                <li>Loggning av all åtkomst till känslig data</li>
                <li>Regelbundna säkerhetsgranskningar</li>
              </ul>

              <h3 className="text-lg font-medium mt-4 mb-2">6.3 Lagringstid</h3>
              <p>
                Vi lagrar dina personuppgifter så länge som:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Du är aktiv i programmet</li>
                <li>Det finns en dokumentationsskyldighet enligt tillämplig lagstiftning</li>
                <li>Normalt: 10 år efter avslutat program, därefter anonymiseras data</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">7. Dina Rättigheter enligt GDPR</h2>
              <p className="mb-2">
                Du har följande rättigheter:
              </p>

              <h3 className="text-lg font-medium mt-4 mb-2">7.1 Rätt till Tillgång</h3>
              <p>Du har rätt att få en kopia av alla personuppgifter vi har om dig.</p>

              <h3 className="text-lg font-medium mt-4 mb-2">7.2 Rätt till Rättelse</h3>
              <p>Du kan begära att vi korrigerar felaktiga eller ofullständiga uppgifter.</p>

              <h3 className="text-lg font-medium mt-4 mb-2">7.3 Rätt till Radering</h3>
              <p>
                Du kan begära radering av dina uppgifter, med förbehåll för eventuell
                dokumentationsskyldighet enligt tillämplig lagstiftning.
              </p>

              <h3 className="text-lg font-medium mt-4 mb-2">7.4 Rätt till Begränsning</h3>
              <p>Du kan begära att behandlingen av dina uppgifter begränsas under vissa omständigheter.</p>

              <h3 className="text-lg font-medium mt-4 mb-2">7.5 Rätt till Dataportabilitet</h3>
              <p>Du kan begära att få dina uppgifter i ett strukturerat, maskinläsbart format.</p>

              <h3 className="text-lg font-medium mt-4 mb-2">7.6 Rätt att Invända</h3>
              <p>Du kan invända mot behandling som baseras på allmänt intresse eller berättigat intresse.</p>

              <h3 className="text-lg font-medium mt-4 mb-2">7.7 Rätt att Återkalla Samtycke</h3>
              <p>Du kan när som helst återkalla ditt samtycke till behandling.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">8. Cookies och Spårningsteknologi</h2>
              <p className="mb-2">
                Vi använder följande cookies:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li><strong>Nödvändiga cookies:</strong> För inloggning och sessionshantering</li>
                <li><strong>Funktionella cookies:</strong> För att komma ihåg dina inställningar</li>
                <li><strong>Analytiska cookies:</strong> För att förbättra plattformen (anonymiserad data)</li>
              </ul>
              <p className="mt-2">
                Vi använder <strong>inte</strong> cookies för reklam eller spårning mellan webbplatser.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">9. Barns Integritet</h2>
              <p>
                Plattformen är inte avsedd för personer under 18 år. Vi samlar inte medvetet in
                personuppgifter från barn. Kontakta oss om du tror att vi har data om någon under 18 år.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">10. Ändringar i Integritetspolicyn</h2>
              <p>
                Vi kan uppdatera denna policy när ny funktionalitet läggs till eller lagstiftning ändras.
                Väsentliga ändringar kommer att kommuniceras via plattformen och kräva nytt samtycke.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">11. Kontakta Oss</h2>
              <p className="mb-2">
                För frågor om denna integritetspolicy eller för att utöva dina rättigheter:
              </p>
              <p>
                <strong>Personuppgiftsansvarig:</strong> Operatören av H.J.Ä.L.P.<br />
                <strong>Webbplats:</strong> sinnerjin.com<br />
                <strong>Kontakt:</strong> Via kontaktformuläret på sinnerjin.com
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">12. Klagomål till Tillsynsmyndighet</h2>
              <p>
                Om du anser att behandlingen av dina personuppgifter strider mot GDPR har du rätt att
                lämna klagomål till:
              </p>
              <p className="mt-2">
                <strong>Integritetsskyddsmyndigheten (IMY)</strong><br />
                Box 8114<br />
                104 20 Stockholm<br />
                E-post: imy@imy.se<br />
                Telefon: 08-657 61 00
              </p>
            </section>

            <section className="border-t pt-6 mt-8">
              <p className="text-sm text-muted-foreground">
                Genom att använda H.J.Ä.L.P.-portalen bekräftar du att du har läst och förstått denna
                integritetspolicy och samtycker till behandlingen av dina personuppgifter enligt beskrivningen ovan.
              </p>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
